/**
 * Custom hook to sync investor draft data with Redux store
 * Consolidates all Redux dispatch operations for draft loading/saving
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setFinancialStatementDoc,
  setInvestmentMandateDoc,
  setFundManagerLicenseDoc,
  setCertificateOfIncorporationDoc,
  setCompanyConstitutionDoc,
  setCrsSelfCertificationFormDoc,
  setProofOfBankAccountDetailsDoc,
} from "../../../features/merchant/doc";
import { hydrateInstitutionalBizState } from "../../../features/institutionalInvestor/bizSlice";
import { hydrateInstitutionalRepState } from "../../../features/institutionalInvestor/repSlice";
import { hydrateInstitutionalBankState } from "../../../features/institutionalInvestor/bankSlice";

export interface InvestorDraftData {
  // Documents
  businessProfile?: any[];
  proofOfAddress?: any[];
  shareHoldersDirectorsRegisters?: any[];
  financialStatement?: any[];
  investmentMandate?: any[];
  fundManagerLicense?: any[];
  certificateOfIncorporation?: any[];
  companyConstitution?: any[];
  crsSelCertificationForm?: any[]; // Note: typo in backend field name
  proofOfBankAccountDetails?: any[];

  // Business info
  companyName?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  legalStructureOthers?: string;
  countryOfIncorporation?: string;
  countryOfDomicile?: string;
  region?: string;
  city?: string;
  legalEntityAddress?: string;
  postalCode?: string;
  website?: string;
  phoneNumber?: string;
  companyPhoneNumber?: string;
  industryGroup?: string;
  industry?: string;
  investmentAmount?: number;
  companyStartDate?: string;

  // Representative info
  repFirstName?: string;
  repLastName?: string;
  repEmail?: string;
  repTitle?: string;
  userTitle?: string;
  shareHoldersDirectors?: any[];

  // Bank info
  bankName?: string;
  bankCode?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;
}

/**
 * Hook to sync investor draft data with Redux store
 */
export const useInvestorDraftSync = () => {
  const dispatch = useDispatch();

  /**
   * Sync all draft data to Redux store
   * Uses hydrate pattern for efficient bulk updates
   */
  const syncInvestorDraftToRedux = useCallback(
    (data: InvestorDraftData) => {
      // Sync document uploads (10 documents) - Use unwrapped payloads
      dispatch(setBusinessDoc(data.businessProfile || []));
      dispatch(setProofDoc(data.proofOfAddress || []));
      dispatch(setShareDoc(data.shareHoldersDirectorsRegisters || []));
      dispatch(setFinancialStatementDoc(data.financialStatement || []));
      dispatch(setInvestmentMandateDoc(data.investmentMandate || []));
      dispatch(setFundManagerLicenseDoc(data.fundManagerLicense || []));
      dispatch(
        setCertificateOfIncorporationDoc(data.certificateOfIncorporation || [])
      );
      dispatch(setCompanyConstitutionDoc(data.companyConstitution || []));
      dispatch(
        setCrsSelfCertificationFormDoc(data.crsSelCertificationForm || [])
      );
      dispatch(
        setProofOfBankAccountDetailsDoc(data.proofOfBankAccountDetails || [])
      );

      // Sync business info - Single dispatch with hydrateInstitutionalBizState
      dispatch(
        hydrateInstitutionalBizState({
          company: data.companyName ?? null,
          regNumber: data.companyRegistrationNo ?? null,
          legalStructure: data.legalStructure ?? null,
          legalStructureOthers: data.legalStructureOthers ?? null,
          countryOfIncorp: data.countryOfIncorporation ?? null,
          countryOfDomicile: data.countryOfDomicile ?? null,
          region: data.region ?? null,
          city: data.city ?? null,
          address: data.legalEntityAddress ?? null,
          postal: data.postalCode?.toString() ?? null,
          website: data.website ?? null,
          phoneNumber: data.phoneNumber ?? data.companyPhoneNumber ?? null,
          industryGroup: data.industryGroup ?? null,
          industry: data.industry ?? null,
          investmentAmount: data.investmentAmount ? Number(data.investmentAmount) : null,
          incorporationDate: data.companyStartDate ?? null,
          industryOthers: data.industry ?? null,
        })
      );

      // Sync representative info - Single dispatch with hydrateInstitutionalRepState
      dispatch(
        hydrateInstitutionalRepState({
          repFirstName: data.repFirstName ?? null,
          repLastName: data.repLastName ?? null,
          repEmail: data.repEmail ?? null,
          repTitle: data.repTitle ?? null,
          userTitle: data.userTitle ?? null,
          shareholdersDirectors: data.shareHoldersDirectors || [],
        })
      );

      // Sync bank info - Single dispatch with hydrateInstitutionalBankState
      dispatch(
        hydrateInstitutionalBankState({
          bankName: data.bankName ?? null,
          bankCode: data.bankCode ?? null,
          accountType: data.accountType ?? null,
          accountNumber: data.accountNumber ?? null,
          accountHolderName: data.accountHolderName ?? null,
          swiftCode: data.swiftCode ?? null,
        })
      );
    },
    [dispatch]
  );

  return { syncInvestorDraftToRedux };
};
